import type { ReactElement } from "react"
import type { Control, FieldPath, FieldValues } from "react-hook-form"
import { useController } from "react-hook-form"

import { AerosSelect, type AerosSelectProps } from "../AerosSelect"

interface FormSelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string | number = string,
> extends Omit<AerosSelectProps<TValue>, "name" | "defaultValue" | "value" | "onChange"> {
  control: Control<TFieldValues>
  name: TName
  onChange?: (value: TValue | null) => void
}

export const FormSelect = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string | number = string,
>({
  control,
  name,
  helperText,
  onChange,
  ...props
}: FormSelectProps<TFieldValues, TName, TValue>): ReactElement => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  })

  const handleChange = (value: TValue | null) => {
    field.onChange(value)
    onChange?.(value)
  }

  return (
    <AerosSelect<TValue>
      {...field}
      {...props}
      error={!!error}
      helperText={error?.message ?? helperText}
      onChange={handleChange}
    />
  )
}
